import { MsalProvider } from '@azure/msal-react';
import { EventType } from '@azure/msal-browser';
import { useEffect, useState } from 'react';
import { pca } from './msalConfig';
import { clearIdleLock } from './idleLockState';

/**
 * Wraps the app in MsalProvider. Initializes MSAL and processes the
 * post-login redirect response *before* rendering children, so that
 * RequireRole sees a populated account on the first render.
 */
export default function AuthProvider({ children }) {
  const [ready, setReady] = useState(false);

  useEffect(() => {
    let callbackId;

    (async () => {
      // 1) MSAL v3 requires explicit initialization before any use.
      await pca.initialize();

      // 2) Catch the redirect "ticket" from Microsoft, if we just came back from a sign-in.
      try {
        // Who was signed in before this redirect (if anyone) — used to
        // catch the case where the idle-lock re-verification was
        // completed by a DIFFERENT person than the locked session's
        // owner.
        const prior = pca.getActiveAccount() || pca.getAllAccounts()[0] || null;
        const response = await pca.handleRedirectPromise();
        if (response?.account) {
          const samePerson =
            !prior ||
            prior.homeAccountId === response.account.homeAccountId;
          if (!samePerson) {
            // Someone else authenticated mid-session (e.g. during an
            // idle-lock unlock). Don't hand them the previous user's
            // session — sign out completely and land on Sign In.
            clearIdleLock();
            await pca.logoutRedirect({
              postLogoutRedirectUri: window.location.origin,
            });
            return; // navigating away — don't render the app
          }
          // A fresh interactive sign-in is at least as strong as the
          // idle lock's password re-verification, so any pending lock
          // is cleared here — this is the ONLY place an unlock
          // completes. Cancelling at the Microsoft page returns with
          // no response, leaving the lock in place.
          clearIdleLock();
          pca.setActiveAccount(response.account);
        }
      } catch (err) {
        console.error('handleRedirectPromise failed:', err);
      }

      // 3) If we already had an account cached, make it the active one.
      if (!pca.getActiveAccount()) {
        const accounts = pca.getAllAccounts();
        if (accounts.length > 0) {
          pca.setActiveAccount(accounts[0]);
        }
      }

      // 4) Keep the active account in sync with future logins.
      callbackId = pca.addEventCallback((event) => {
        if (
          event.eventType === EventType.LOGIN_SUCCESS &&
          event.payload?.account
        ) {
          pca.setActiveAccount(event.payload.account);
        }
      });

      // 5) Only now do we let the rest of the app render.
      setReady(true);
    })();

    return () => {
      if (callbackId) pca.removeEventCallback(callbackId);
    };
  }, []);

  if (!ready) {
    return (
      <div style={{ padding: 24, fontFamily: 'system-ui', color: '#555' }}>
        Signing you in…
      </div>
    );
  }

  return <MsalProvider instance={pca}>{children}</MsalProvider>;
}